import { useMemo, useState } from 'react';
import type { Service, Zona } from '@booking/shared';
import { acomodarZonas, cuentaDeZonas, zonasBloqueadas } from '@booking/shared';

type Props = {
  service: Service;
  /* lo ya escogido, por si se vuelve desde el calendario */
  escogidas?: string[];
  onPick: (zonas: string[], minutos: number) => void;
  onBack: () => void;
};

const TITULOS: Record<Zona['tamano'], string> = {
  grande: 'Áreas grandes',
  mediana: 'Áreas medianas',
  pequena: 'Áreas pequeñas',
};
const ORDEN: Zona['tamano'][] = ['grande', 'mediana', 'pequena'];

/**
 * Depilación láser: una sola entrada en la lista de servicios que acá se abre
 * en todas las zonas. El precio y el tiempo se van sumando conforme se marca, y
 * las zonas que ya quedan cubiertas por otra se bloquean solas (al marcar
 * piernas completas no tiene sentido marcar media pierna).
 */
export function ZonePicker({ service, escogidas = [], onPick, onBack }: Props) {
  const porZonas = service.porZonas!;
  const [marcadas, setMarcadas] = useState<string[]>(escogidas);

  const bloqueadas = useMemo(() => zonasBloqueadas(porZonas, marcadas), [porZonas, marcadas]);
  const cuenta = useMemo(() => cuentaDeZonas(porZonas, marcadas), [porZonas, marcadas]);

  function alternar(id: string) {
    setMarcadas((antes) => {
      const siguiente = antes.includes(id) ? antes.filter((x) => x !== id) : [...antes, id];
      /* acá se juntan las partes: media pierna inferior + superior = piernas completas */
      return acomodarZonas(porZonas, siguiente);
    });
  }

  const precio = (t: Zona['tamano']) => `$${porZonas.tamanos[t].precio.toLocaleString('en-US')}`;

  return (
    <div className="zone-picker">
      <button className="back-btn" onClick={onBack}>‹ Volver</button>
      <h2>¿Qué zonas querés depilar?</h2>
      <p className="zone-hint">
        Marcá todas las que quieras: el precio y la duración se suman. La valoración
        con la enfermera está incluida.
        {porZonas.nota && <> <strong>{porZonas.nota}.</strong></>}
      </p>

      {ORDEN.map((tamano) => {
        const zonas = porZonas.zonas.filter((z) => z.tamano === tamano);
        if (!zonas.length) return null;
        return (
          <fieldset key={tamano} className="zone-group">
            <legend>
              {TITULOS[tamano]} <span className="zone-price">{precio(tamano)} c/u</span>
            </legend>
            <div className="zone-list">
              {zonas.map((z) => {
                const bloqueada = bloqueadas.includes(z.id);
                return (
                  <label key={z.id} className={`zone${bloqueada ? ' blocked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={marcadas.includes(z.id)}
                      disabled={bloqueada}
                      onChange={() => alternar(z.id)}
                    />
                    <span>{z.nombre}</span>
                    {bloqueada && <em className="zone-note">ya incluida</em>}
                  </label>
                );
              })}
            </div>
          </fieldset>
        );
      })}

      <div className="zone-total">
        <div>
          <strong>
            {marcadas.length === 0
              ? 'Nada marcado todavía'
              : `${marcadas.length} ${marcadas.length === 1 ? 'zona' : 'zonas'}`}
          </strong>
          <span>
            {marcadas.length > 0 && (
              <>
                ${cuenta.precio.toLocaleString('en-US')} · {cuenta.minutos} minutos
                {porZonas.nota ? ' · 6 sesiones por zona' : ''}
              </>
            )}
          </span>
        </div>
        <button
          className="primary"
          disabled={marcadas.length === 0}
          onClick={() => onPick(marcadas, cuenta.minutos)}
        >
          Ver horarios
        </button>
      </div>
    </div>
  );
}
